<template>
  <div class="overflow-x-hidden">
    <!-- Hero -->
    <section class="relative bg-hero-fade pt-20 pb-14 lg:pt-28">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>
      <div class="relative container-wide max-w-4xl text-center">
        <div class="eyebrow mx-auto w-fit mb-4">About</div>
        <h1 class="font-display text-5xl lg:text-6xl text-ink-900 leading-[1.05] text-balance">
          We believe search should <span class="italic text-brand-700">reward work</span>, not tooling.
        </h1>
        <p class="mt-6 text-lg text-ink-600 max-w-2xl mx-auto text-pretty">
          LeadRanks was built because every existing tool felt like it was solving yesterday's problem.
          We wanted the depth of an enterprise audit, the speed of a single click, and recommendations you could actually act on.
        </p>
      </div>
    </section>

    <!-- Mission -->
    <section class="py-20 lg:py-28">
      <div class="container-wide max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <div class="eyebrow mb-3">Our mission</div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">
            Honest signal, ranked by impact, delivered fast.
          </h2>
          <p class="mt-5 text-ink-600 text-lg leading-relaxed">
            Most SEO tools dump 200-item checklists and leave you to guess what matters. We rank every finding by estimated ranking impact — so you know what to fix first.
          </p>
          <p class="mt-4 text-ink-600 leading-relaxed">
            Our pipeline runs Lighthouse, PageSpeed, structured data validators, link integrity checks, and backlink intelligence in parallel. Not because it's clever — because your time is worth more than a loading spinner.
          </p>
        </div>
        <div class="relative">
          <div class="absolute -inset-8 bg-gradient-to-br from-brand-100/40 to-accent-100/30 rounded-3xl blur-2xl"></div>
          <div class="relative card p-6 space-y-4">
            <div v-for="(stat, i) in stats" :key="stat.label" class="flex items-start gap-4">
              <span class="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                <Icon :name="stat.icon" :size="18" />
              </span>
              <div>
                <div class="font-display text-2xl text-ink-900 leading-none">{{ stat.value }}</div>
                <div class="text-sm text-ink-600 mt-1">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Values -->
    <section class="py-20 bg-ink-900 text-white relative overflow-hidden">
      <div class="absolute inset-0 bg-dot-ink bg-dot-20 opacity-[0.04]"></div>
      <div class="relative container-wide max-w-6xl">
        <div class="max-w-2xl mb-12">
          <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-500 bg-brand-500/10 px-3 py-1 rounded-full border border-brand-500/20 mb-4">
            Principles
          </div>
          <h2 class="font-display text-4xl lg:text-5xl text-balance leading-tight text-white">
            How we build.
          </h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div v-for="v in values" :key="v.title" class="rounded-xl border border-ink-700 bg-ink-800/50 p-6">
            <span class="w-11 h-11 rounded-xl bg-brand-500/20 text-brand-500 flex items-center justify-center mb-4">
              <Icon :name="v.icon" :size="20" />
            </span>
            <h3 class="text-lg font-semibold text-white">{{ v.title }}</h3>
            <p class="mt-2 text-sm text-ink-300 leading-relaxed">{{ v.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team -->
    <section class="py-24">
      <div class="container-wide max-w-5xl">
        <div class="max-w-xl mb-12">
          <div class="eyebrow mb-3">Team</div>
          <h2 class="font-display text-4xl text-ink-900">Built by people who've shipped SEO.</h2>
          <p class="mt-4 text-ink-600 leading-relaxed">
            A small, senior team of engineers, SEO consultants, and former agency leads —
            we've spent careers in the trenches running audits for clients ourselves, which
            is exactly why we got tired of the tools available and built our own.
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div v-for="d in disciplines" :key="d.title" class="card p-6">
            <span class="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
              <Icon :name="d.icon" :size="18" />
            </span>
            <div class="font-semibold text-ink-900 mt-4">{{ d.title }}</div>
            <p class="text-sm text-ink-600 mt-1.5 leading-relaxed">{{ d.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="pb-24">
      <div class="container-tight">
        <div class="card p-10 lg:p-14 text-center bg-gradient-to-br from-brand-50 to-white border border-brand-100">
          <h2 class="font-display text-4xl text-ink-900">Try what we built.</h2>
          <p class="mt-3 text-ink-600">Free-forever tier. Your first audit in 3–5 minutes.</p>
          <div class="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <router-link to="/register" class="btn-primary btn-lg">Start free <Icon name="arrow-right" :size="16" /></router-link>
            <router-link to="/contact" class="btn-outline btn-lg">Say hello</router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';

usePageMeta({
  title: 'About — LeadRanks',
  meta: [
    { name: 'description', content: 'Why LeadRanks exists: signal over noise, speed as a feature, and honesty about what an SEO audit can and cannot tell you. See how and why we build it.' },
    { property: 'og:title', content: 'About — LeadRanks' },
    { property: 'og:description', content: 'Why LeadRanks exists: signal over noise, speed as a feature, and honesty about what an SEO audit can and cannot tell you. See how and why we build it.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
});

// These are deliberately evergreen, verifiable claims rather than a live
// production counter -- a "10,000+ sites audited" / "92% caught first run"
// style stat needs a real, currently-tracked number behind it or it's just
// a made-up placeholder that goes stale (or stays fabricated) silently.
// "100+" and "17" are the same figures already used consistently elsewhere
// on the site (HomePage.vue, FeaturesPage.vue, AnalyzePage.vue, etc.) --
// reusing them here instead of inventing a fourth number.
const stats = [
  { icon: 'trending-up', value: '100+',  label: 'Signals checked, every audit' },
  { icon: 'clock',       value: '4 min', label: 'Median audit runtime' },
  { icon: 'layers',      value: '17',    label: 'Parallel analysis modules' },
];

const values = [
  { icon: 'target',   title: 'Signal over noise',       body: 'Every recommendation is ranked by estimated impact. We kill the 200-item checklist.' },
  { icon: 'zap',      title: 'Speed is a feature',      body: 'Parallel pipelines. Live progress. Full audits in minutes — not over an email tomorrow.' },
  { icon: 'shield',   title: 'Honest about our limits', body: "We tell you when a signal is weak, when an API flakes, and when you don't need to fix something." },
];

const disciplines = [
  { icon: 'code',    title: 'Engineering',        body: 'Built the audit pipeline, the AI copilot, and everything that keeps a 100+ point analysis to 3–5 minutes.' },
  { icon: 'search',  title: 'SEO Research',       body: 'Ex-agency practitioners who know which findings actually move rankings — and which are noise.' },
  { icon: 'smile',   title: 'Customer Success',   body: 'The people who read every support message and turn user feedback into the next release.' },
];
</script>
